/**
 * The `/admin` route group — the ops console's shell (PLAN-OPS Phase 1).
 *
 * A layout route, so the guard and the tab bar are written once and every page
 * under it inherits both. `autoCodeSplitting` in `vite.config.ts` means the
 * whole group is its own chunk: a user who never opens the console never
 * downloads it.
 *
 * ## The guard is a courtesy
 *
 * This component decides what to *render*, not what is *permitted*. Every
 * `/api/admin/*` request is checked against `session.user.role` in the Worker,
 * which is the only place a check can be trusted. What this buys is a sensible
 * page for the three people who can end up here: the admin (the console), a
 * signed-in user who followed a stray link (a plain "not available"), and a
 * signed-out visitor (a way in). Deleting this file would cost nothing in
 * security and quite a lot in coherence.
 */

import { createFileRoute, Link, Outlet } from '@tanstack/react-router';
import { useSession } from '@/auth/session-context';
import { AdminPage, PageHeader } from '@/components/admin/primitives';
import { OpeningSplash } from '@/components/opening-splash';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin')({
  component: AdminLayout,
});

const TABS = [
  { to: '/admin', label: 'Overview', exact: true },
  { to: '/admin/users', label: 'Users', exact: false },
  { to: '/admin/billing', label: 'Billing', exact: false },
  { to: '/admin/support', label: 'Support', exact: false },
  { to: '/admin/board', label: 'Board', exact: false },
  { to: '/admin/gallery', label: 'Gallery', exact: false },
  { to: '/admin/usage', label: 'AI Spend', exact: false },
  { to: '/admin/health', label: 'Health', exact: false },
] as const;

function AdminLayout() {
  const session = useSession();

  if (session.status === 'loading') {
    return <OpeningSplash label="Checking your account…" />;
  }

  if (session.user === null) {
    return (
      <AdminPage>
        <PageHeader
          title="Sign in"
          description="The Admin Console is only reachable by an administrator of this deployment."
        />
        <div>
          <Button asChild size="sm">
            <Link to="/signin">Sign in</Link>
          </Button>
        </div>
      </AdminPage>
    );
  }

  if (session.user.role !== 'admin') {
    return (
      <AdminPage>
        <PageHeader
          title="Not available"
          description="This account is not an administrator on this deployment. Nothing under /admin will answer for it."
        />
        <div>
          <Button asChild size="sm" variant="outline">
            <Link to="/recipes">Back to your recipes</Link>
          </Button>
        </div>
      </AdminPage>
    );
  }

  return (
    <div className="flex min-h-full flex-col">
      <nav
        aria-label="Admin Console"
        // `overflow-x-auto` + `whitespace-nowrap` on the tabs: eight tabs do
        // not fit a phone, and without a scroll the tail of the console —
        // Health, exactly the tab an operator reaches for on the move — was
        // simply unreachable. Wrapping was rejected: a two-row tab bar reads
        // as two different bars.
        className="sticky top-0 z-10 flex items-center gap-1 overflow-x-auto border-b border-border bg-background px-4 sm:px-8"
      >
        {TABS.map((tab) => (
          <Link
            key={tab.to}
            to={tab.to}
            activeOptions={{ exact: tab.exact }}
            className={cn(
              'shrink-0 whitespace-nowrap border-b-2 border-transparent px-2 py-3 text-sm font-medium text-muted-foreground',
              'transition-colors hover:text-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            )}
            activeProps={{ className: 'border-primary text-foreground' }}
          >
            {tab.label}
          </Link>
        ))}
      </nav>
      <Outlet />
    </div>
  );
}
